import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Button,
    Chip,
    Stack,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton
} from '@mui/material';
import { keyframes } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import {
    Phone,
    MessageCircle,
    MessageSquare,
    Mail,
    Globe,
    Users
} from 'lucide-react';
import NewCamp from '../../../assets/NewCamp.png';

const marqueeLeftAnim = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const marqueeRightAnim = keyframes`
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
`;

const marqueeContainerSx = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    position: 'relative',
    width: '100%',
    padding: '10px 0',
    maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
    WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
    '&:hover .marquee-content': {
        animationPlayState: 'paused',
    }
};

const marqueeContentSx = {
    display: 'inline-flex',
    gap: '1.5rem',
    width: 'max-content',
};

const BuiltForYou = () => {
    const industries = [
        "Printing Services", "Real Estate", "Roadside Assistance", "Travel & Tourism",
        "Retail", "VAT & Taxation", "Maintenance", "Healthcare", "Construction Material",
        "Dropshipping", "Education & Training", "E-Commerce", "Interior Designing",
        "Visa Assistance", "Supply & Manufacturing", "Information Technology"
    ];

    const benefits = [
        {
            title: "Never Miss a Customer Again",
            desc: "Every call is answered by trained real human agents, so your business never loses a lead to a missed call."
        },
        {
            title: "Save Thousands on Hiring",
            desc: "No need to build a customer support team. Justtawk gives you trained agents, CRM, and infrastructure in one simple monthly plan."
        },
        {
            title: "Go Live in Minutes",
            desc: "Your business can start receiving and managing customer calls in as little as 5 minutes."
        },
        {
            title: "Multilingual Support for UAE Customers",
            desc: "Support customers in Arabic, English, Urdu, and Hindi, making your business accessible to a wider audience."
        },
        {
            title: "Capture Every Lead Automatically",
            desc: "Every call, enquiry, and customer interaction is logged, recorded, and organized inside your CRM."
        },
        {
            title: "Focus on Growing Your Business",
            desc: "While Justtawk handles your calls, enquiries, and customer communication, you focus on what matters most — running and growing your business."
        }
    ];
    const [showCalendly, setShowCalendly] = useState(false);
    const calendlyRef = useRef(null);

    useEffect(() => {
        // Load YouTube IFrame API
        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = "https://www.youtube.com/iframe_api";
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }

        let checkInterval;

        const initializePlayer = () => {
             new window.YT.Player('youtube-player', {
                events: {
                    'onStateChange': (event) => {
                        const player = event.target;
                        if (event.data === window.YT.PlayerState.PLAYING) {
                            if (checkInterval) clearInterval(checkInterval);
                            checkInterval = setInterval(() => {
                                try {
                                    if (player && typeof player.getDuration === 'function' && typeof player.getCurrentTime === 'function') {
                                        const duration = player.getDuration();
                                        const currentTime = player.getCurrentTime();
                                        if (duration > 0 && (currentTime / duration) >= 0.3) {
                                            setShowCalendly(true);
                                            clearInterval(checkInterval);
                                            checkInterval = null;
                                        }
                                    }
                                } catch (e) {
                                    console.error("Error checking video progress:", e);
                                }
                            }, 1000);
                        } else {
                            if (checkInterval) clearInterval(checkInterval);
                            checkInterval = null;
                        }
                    }
                }
            });
        };

        if (window.YT && window.YT.Player) {
            initializePlayer();
        } else {
            window.onYouTubeIframeAPIReady = initializePlayer;
        }

        // Load Calendly Widget Script on Mount for pre-loading
        const calendlyScript = document.createElement('script');
        calendlyScript.src = "https://assets.calendly.com/assets/external/widget.js";
        calendlyScript.async = true;
        document.body.appendChild(calendlyScript);

        return () => {
            if (checkInterval) clearInterval(checkInterval);
        };
    }, []);


    const handleCloseCalendly = () => {
        setShowCalendly(false);
    };

    return (
        <>
            <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
                {/* Hero Section */}
                <Box
                    component="section"
                    sx={{
                        mb: 0,
                        backgroundImage: `url(${NewCamp})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        borderRadius: 8,
                        py: { xs: 6, md: 7 },
                        px: { xs: 2, md: 4 },
                    }}
                >
                    <Typography
                        variant="h2"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            mb: 2,
                            color: "#333333",
                            fontSize: { xs: '2.5rem', md: '2.8rem' }
                        }}
                    >
                        Want Customer Support <Box component="span" sx={{ color: '#253D8F' }}>Live in 5 Minutes?</Box><br />
                        Watch This Before Booking a Demo
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#2d2d2db3', // Slightly transparent dark color
                            maxWidth: 800,
                            mx: 'auto',
                            mb: 0,
                            fontSize: '1.1rem',
                            lineHeight: 1.6
                        }}
                    >
                        In just 2 minutes, see how Justtawk gives your business real human agents,
                        a UAE business number, multilingual support, and CRM all without hiring
                        or setting up a call center.
                    </Typography>

                    <Box
                        sx={{
                            position: 'relative',
                            // bgcolor: '#f5f7fa',
                            borderRadius: 6,
                            p: { xs: 2, md: 6 },
                            mb: 6,
                            minHeight: { xs: 250, md: 250 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'visible'
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '1000px',
                                mx: 'auto',
                                aspectRatio: '16/9',
                                borderRadius: 4,
                                overflow: 'hidden',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                zIndex: 1
                            }}
                        >
                            <iframe
                                id="youtube-player"
                                width="100%"
                                height="100%"
                                src="https://www.youtube.com/embed/ScMzIvxBSi4?enablejsapi=1"
                                title="YouTube video player"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            ></iframe>
                        </Box>


                    </Box>

                    <Stack spacing={1} alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            Get started in 5 minutes!
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            sx={{
                                bgcolor: '#2b4194',
                                borderRadius: 50,
                                // px: 1,
                                // py: 2,
                                fontSize: '1.3rem',
                                fontWeight: 600,
                                '&:hover': { bgcolor: '#1e2e6b', transform: 'scale(1.05)' },
                                transition: 'all 0.3s ease',
                                textTransform: 'capitalize'
                            }}
                        >
                            Schedule a Demo
                        </Button>
                    </Stack>
                </Box>

                <Dialog
                    open={showCalendly}
                    onClose={handleCloseCalendly}
                    fullScreen
                    keepMounted
                >
                    <IconButton
                        onClick={handleCloseCalendly}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: (theme) => theme.palette.grey[500],
                            zIndex: 2,
                            bgcolor: 'rgba(0,0,0,0.05)',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.1)',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent 
                        sx={{ 
                            p: 0, 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                        }}
                    >
                        <div 
                            className="calendly-inline-widget" 
                            data-url="https://calendly.com/corpcomm-technotaskbusinesssolutions/justtawk-demo?hide_gdpr_banner=1" 
                            style={{ minWidth: '320px', width: '100%', height: '100%' }}
                        ></div>
                    </DialogContent>
                </Dialog>

                {/* Industries Section */}
                <Box component="section" sx={{ my: 12, overflow: '' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 6 }}>
                        We Successfully Serve
                    </Typography>

                    {/* Row 1: Left to Right */}
                    <Box sx={marqueeContainerSx}>
                        <Box 
                            className="marquee-content"
                            sx={{
                                ...marqueeContentSx,
                                animation: `${marqueeLeftAnim} 30s linear infinite`,
                            }}
                        >
                            {[...industries.slice(0, 6), ...industries.slice(0, 6)].map((item, idx) => (
                                <Chip key={idx} label={item} sx={{ bgcolor: '#f1f1f1', fontWeight: 500, px: 1, py: 2.5, borderRadius: 2, mx: 1, fontSize: "1.2rem" }} />
                            ))}
                        </Box>
                    </Box>

                    {/* Row 2: Right to Left */}
                    <Box sx={{ ...marqueeContainerSx, mt: 0 }}>
                        <Box 
                            className="marquee-content"
                            sx={{
                                ...marqueeContentSx,
                                animation: `${marqueeRightAnim} 40s linear infinite`,
                            }}
                        >
                            {[...industries.slice(6, 11), ...industries.slice(6, 11)].map((item, idx) => (
                                <Chip key={idx} label={item} sx={{ bgcolor: '#f1f1f1', fontWeight: 500, px: 1, py: 2.5, borderRadius: 2, mx: 1, fontSize: "1.2rem" }} />
                            ))}
                        </Box>
                    </Box>

                    {/* Row 3: Left to Right */}
                    <Box sx={{ ...marqueeContainerSx, mt: 0 }}>
                        <Box 
                            className="marquee-content"
                            sx={{
                                ...marqueeContentSx,
                                animation: `${marqueeLeftAnim} 35s linear infinite`,
                            }}
                        >
                            {[...industries.slice(11), ...industries.slice(11)].map((item, idx) => (
                                <Chip key={idx} label={item} sx={{ bgcolor: '#f1f1f1', fontWeight: 500, px: 1, py: 2.5, borderRadius: 2, mx: 1, fontSize: "1.2rem" }} />
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Benefits Section */}
                <Container maxWidth="lg">
                    <Box component="section" sx={{ my: 12 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 8, color: "#333333" }}>
                            What’s In It For You?
                        </Typography>
                        <Grid container spacing={6} sx={{ textAlign: 'left' }}>
                            {benefits.map((benefit, idx) => (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2b4194', mb: 1.5 }}>
                                        {benefit.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                        {benefit.desc}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>


            </Container>
            {/* Bottom CTA */}
            <Box
                component="section"
                sx={{
                    bgcolor: '#f5f7fa',
                    borderRadius: 8,
                    p: { xs: 4, md: 8 },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: { xs: 'center', md: 'left' },
                    gap: 4,
                    // mt: 3
                }}
            >
                <Box sx={{ maxWidth: 550 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#2b4194', mb: 1.5 }}>
                        Customer Experience Should Never Be a Bottleneck For Your Business.
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Justtawk gives your business the power of a full customer support team, without the cost or complexity of building one.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    sx={{
                        bgcolor: '#2b4194',
                        borderRadius: 8,
                        px: 3,
                        py: 1,
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#1e2e6b' },
                        textTransform: 'capitalize'
                    }}
                >
                    Start Your Free Trial
                </Button>
            </Box>
        </>

    );
};

export default BuiltForYou;